import { useEffect, useRef, useState } from "react";
import type { AppEvent } from "../api";
import { isElectronDesktop } from "../desktop";

export type LocalListOpts = {
  limit?: number;
  afterId?: number;
  stockStage?: string | string[];
  includeHidden?: boolean;
  onlyHidden?: boolean;
  status?: string;
};

function keyOf(opts: LocalListOpts): string {
  return JSON.stringify({
    limit: opts.limit ?? 2000,
    afterId: opts.afterId ?? 0,
    stockStage: opts.stockStage ?? null,
    includeHidden: Boolean(opts.includeHidden),
    onlyHidden: Boolean(opts.onlyHidden),
    status: opts.status ?? null,
  });
}

type Entry = {
  key: string;
  opts: LocalListOpts;
  events: AppEvent[];
  listeners: Set<() => void>;
  inFlight: Promise<void> | null;
};

/**
 * Shared local-SQLite viewer cache for Electron.
 * Tab switches read memory instantly; WS→SQLite→db-changed refreshes
 * active queries only. Never hits the hub HTTP API.
 */
class LocalEventsStore {
  private entries = new Map<string, Entry>();
  private started = false;
  private reloadTimer: ReturnType<typeof setTimeout> | null = null;

  private ensureStarted(): void {
    if (this.started || !isElectronDesktop() || !window.brexDesktop?.onDbChanged) {
      return;
    }
    this.started = true;
    window.brexDesktop.onDbChanged(() => {
      if (this.reloadTimer) clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => {
        this.reloadTimer = null;
        void this.reloadActive();
      }, 120);
    });
  }

  getCached(opts: LocalListOpts): AppEvent[] | null {
    const e = this.entries.get(keyOf(opts));
    return e ? e.events : null;
  }

  subscribe(opts: LocalListOpts, listener: () => void): () => void {
    this.ensureStarted();
    const key = keyOf(opts);
    let entry = this.entries.get(key);
    if (!entry) {
      entry = { key, opts, events: [], listeners: new Set(), inFlight: null };
      this.entries.set(key, entry);
    } else {
      entry.opts = opts;
    }
    entry.listeners.add(listener);
    if (entry.events.length === 0 || !entry.inFlight) {
      void this.fetchEntry(entry);
    }
    return () => {
      entry!.listeners.delete(listener);
    };
  }

  private notify(entry: Entry): void {
    for (const l of entry.listeners) {
      try {
        l();
      } catch {
        /* ignore */
      }
    }
  }

  private async fetchEntry(entry: Entry): Promise<void> {
    if (!window.brexDesktop?.listEvents) return;
    if (entry.inFlight) return entry.inFlight;
    entry.inFlight = (async () => {
      try {
        const { events } = await window.brexDesktop!.listEvents!(entry.opts);
        entry.events = events;
        this.notify(entry);
      } catch {
        /* keep prior cache */
      } finally {
        entry.inFlight = null;
      }
    })();
    return entry.inFlight;
  }

  private async reloadActive(): Promise<void> {
    const active = [...this.entries.values()].filter((e) => e.listeners.size > 0);
    await Promise.all(active.map((e) => this.fetchEntry(e)));
  }
}

const store = new LocalEventsStore();

/**
 * Desktop local-DB view. Cached across tab switches; updated when sync
 * writes SQLite (db-changed). Non-desktop returns empty — use HTTP seeds.
 */
export function useLocalEventsQuery(
  opts: LocalListOpts,
  enabled = true
): { events: AppEvent[]; ready: boolean } {
  const desktop = isElectronDesktop();
  const optsKey = keyOf(opts);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const [events, setEvents] = useState<AppEvent[]>(() => {
    if (!desktop || !enabled) return [];
    return store.getCached(opts) ?? [];
  });
  const [ready, setReady] = useState(() => {
    if (!desktop || !enabled) return true;
    return store.getCached(opts) != null;
  });

  useEffect(() => {
    if (!desktop || !enabled) {
      setEvents([]);
      setReady(true);
      return;
    }
    const apply = () => {
      const cached = store.getCached(optsRef.current);
      if (cached) {
        setEvents(cached);
        setReady(true);
      }
    };
    apply();
    return store.subscribe(optsRef.current, apply);
  }, [desktop, enabled, optsKey]);

  return { events, ready };
}
